import useHeightObserver from '@ria-client/hooks/useHeightObserver';
import { AnalyticsSkuViewed } from '@ria-client/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@ria-client/segment/Analytics/constants';
import { MonetizableEnum } from '@ria-client/segment/enums';
import { MarketplaceEntityTypeEnum } from '@ria-client/supergraph/marketplace/enums';
import { memo, type RefObject } from 'react';
import { useBestOfAwardsConfig } from '../../../BestOfAwards/context';
import { BestOfAwardsRoundupBanner } from '../../../BestOfAwards/roundup-banner';
import type { SummaryTableRowWithDrivers } from '../types';
import { DetailsCell } from './details-cell';
import { ProductCell } from './product-cell';

export interface RowProps {
  index: number;
  row: SummaryTableRowWithDrivers;
}

export const Row = memo(
  function Row({ index, row }: RowProps) {
    const { marketplaceEntity } = row;
    const {
      drivers,
      entityType,
      id,
      institution,
      monetizable: defaultMonetizable,
      name,
      offerForCta,
      product,
      productType,
    } = marketplaceEntity;
    const monetizable = offerForCta?.monetizable ?? defaultMonetizable ?? MonetizableEnum.no;
    const offerId = offerForCta?.id ?? id;
    const offerName = offerForCta?.name ?? name;
    const analyticsPayload = {
      index,
      ...(institution?.id ? { institutionId: institution.id } : {}),
      ...(entityType === MarketplaceEntityTypeEnum.Institutions ? { institutionId: id } : {}),
      offerId,
      ...(product?.id ? { productId: product.id } : {}),
      productType,
      monetizable,
    };
    const { config, hasBestOfAward } = useBestOfAwardsConfig(offerId);
    const bestOfAwardsRef = useHeightObserver({
      onChangeHeight: (height, target) => {
        target.closest('tr')?.style.setProperty('--cell-padding-top', `${height}px`);
      },
    });

    return (
      <AnalyticsSkuViewed
        as="tr"
        className="relative [&:last-of-type_td]:border-b-0"
        additionalAttributes={{ page_type: 'roundup' }}
        {...(institution
          ? { institutionId: institution.id, institutionName: institution.name }
          : {})}
        {...(entityType === MarketplaceEntityTypeEnum.Institutions
          ? { institutionId: id, institutionName: name }
          : {})}
        {...(product ? { productId: product.id, productName: product.name } : {})}
        impressionPosition={index}
        impressionSection="summary_table_section"
        level="offer"
        monetizable={monetizable}
        name={offerName}
        offerId={offerId}
        offerName={offerName}
        productType={productType}
        source={SEGMENT_SKU_VIEWED_SOURCE}
      >
        <ProductCell
          analyticsPayload={analyticsPayload}
          beforeMainContent={
            hasBestOfAward ? (
              <BestOfAwardsRoundupBanner
                className="absolute top-0 left-0 w-full"
                ref={bestOfAwardsRef as RefObject<HTMLDivElement | null>}
                variant="row-compact"
              >
                {config?.awardText}
              </BestOfAwardsRoundupBanner>
            ) : null
          }
          marketplaceEntity={marketplaceEntity}
        />
        <DetailsCell drivers={drivers} />
      </AnalyticsSkuViewed>
    );
  },
  (previous, next) =>
    previous.row.marketplaceEntity.id === next.row.marketplaceEntity.id &&
    previous.index === next.index,
);
